import { ReactNode } from 'react';

import Loading from '../atoms/Loading';
import Text from '../base/Text';
import useProgressEstimate from '../hooks/useProgressEstimate';
import WarningIcon from '../icons/WarningIcon';
import Button from '../input/Button';
import CircularProgressIndicator from '../messaging/CircularProgressIndicator';
import PlayerDialog from '../molecules/PlayerDialog';

export type ProgressInfo = {
  startedAt: number;
  estimatedProcessingTime: number;
  onCancel?: () => void;
};

export type LoadingState = {
  text: string;
  type: 'FAILED' | 'PROCESSING';
  progressInfo?: ProgressInfo;
  buttons?: ReactNode;
};

export default function GenericLoader({
  loadingState: { text, type = 'PROCESSING', progressInfo, buttons },
}: {
  loadingState: LoadingState;
}) {
  const progressPercentage = useProgressEstimate(
    progressInfo?.startedAt,
    progressInfo?.estimatedProcessingTime,
  );

  const width = progressInfo || type === 'FAILED' ? 228 : 182;

  return (
    <PlayerDialog maxWidth={width}>
      <div className="relative flex flex-col items-center pb-2">
        {type === 'FAILED' ? (
          <div className="flex w-[182px] flex-col items-center">
            <WarningIcon className="text-gray-45 h-8 w-8" />
            <Text variant="studio/body/sm" className="mt-5">
              {text}
            </Text>
          </div>
        ) : (
          <>
            {progressInfo ? (
              <div className="flex w-[182px] flex-col items-center">
                <CircularProgressIndicator
                  progress={progressPercentage}
                  strokeWidth={3.5}
                  radius={58}
                />
                <Text variant="studio/body/sm" className="mt-5">
                  {text}
                </Text>
                {progressInfo?.estimatedProcessingTime && (
                  <Text variant="studio/body/xs" className="text-tertiary mt-1">
                    Approx. {progressInfo.estimatedProcessingTime} seconds
                  </Text>
                )}
                {progressInfo.onCancel && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-6"
                    onClick={progressInfo.onCancel}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex w-[182px] flex-col items-center">
                <Loading
                  className="text-green-35 h-10 w-10"
                  trackClassName="stroke-gray-30"
                  strokeWidth={2.5}
                />
                <Text variant="studio/body/sm" className="mt-5">
                  {text}
                </Text>
              </div>
            )}
          </>
        )}

        {buttons}
      </div>
    </PlayerDialog>
  );
}
